import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Briefcase, Building2, Loader2, Banknote, Bookmark, Sparkles, HeartHandshake, GraduationCap } from "lucide-react";
import ShareMenu from "../components/ShareMenu";
import { toast } from "sonner";

const Opportunities = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ job_qualifications: [], job_locations: [], employment_types: [] });
  const [savedIds, setSavedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [qualification, setQualification] = useState("All");
  const [location, setLocation] = useState("All");
  const [empType, setEmpType] = useState("All");

  const isPro = user?.role === "professional";

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const loadSaved = useCallback(() => {
    if (user) api.get("/saved-jobs/ids").then((r) => setSavedIds(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const endpoint = isPro ? "/jobs/recommended" : "/jobs";
      api
        .get(endpoint, { params: { search: search || undefined, qualification, location, employment_type: empType } })
        .then((r) => setJobs(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, qualification, location, empType, isPro]);

  const apply = async (job) => {
    if (!user) {
      toast("Please log in to continue", { description: "Create a professional account to apply or express interest." });
      navigate("/login");
      return;
    }
    if (!isPro) { toast.error("Only professionals can apply"); return; }
    const community = job.source === "community";
    try {
      const r = await api.post(`/jobs/${job.id}/${community ? "interest" : "apply"}`, {});
      if (community) {
        if (r.data.duplicate) toast("Already registered", { description: "You've already expressed interest." });
        else toast.success("Interest registered!", { description: "CareHR will connect you with the institution." });
      } else {
        if (r.data.duplicate) toast("Already applied", { description: "You've already applied to this role." });
        else toast.success("Application sent!", { description: `Your application for ${job.role_title} was submitted.` });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  const toggleSave = async (job) => {
    if (!user) { toast("Log in to save jobs"); navigate("/login"); return; }
    const saved = savedIds.includes(job.id);
    try {
      if (saved) {
        await api.delete(`/saved-jobs/${job.id}`);
        setSavedIds((ids) => ids.filter((i) => i !== job.id));
      } else {
        await api.post("/saved-jobs", { job_id: job.id });
        setSavedIds((ids) => [...ids, job.id]);
        toast.success("Saved", { description: "Find it later under Saved." });
      }
    } catch {
      toast.error("Could not update saved jobs");
    }
  };

  return (
    <PageShell>
      <div className="mx-auto max-w-[1200px] px-6 py-10 lg:px-8">
        <h1 className="font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Find Opportunities</h1>
        <p className="mt-2 text-[16px] text-slate-600">
          {isPro ? "Ranked for you based on your saved preferences." : "Browse roles that match your skills and goals."}
        </p>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search roles..." className="h-11 pl-9" />
          </div>
          <Select value={qualification} onValueChange={setQualification}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Qualification" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All qualifications</SelectItem>
              {meta.job_qualifications.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All locations</SelectItem>
              {meta.job_locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={empType} onValueChange={setEmpType}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All types</SelectItem>
              {meta.employment_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <p className="mt-6 text-sm text-slate-500">{loading ? "Loading..." : `${jobs.length} opportunities found`}</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            {jobs.map((job) => {
              const saved = savedIds.includes(job.id);
              const matched = isPro && job.match_score > 0;
              const community = job.source === "community";
              return (
                <div key={job.id} className={`flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${matched ? "border-[#16A34A]/40 ring-1 ring-[#16A34A]/20" : "border-slate-100"}`}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    {community && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#1E4FA3]/10 px-2.5 py-1 text-xs font-semibold text-[#1E4FA3]"><HeartHandshake className="h-3.5 w-3.5" /> Community · Posted by CareHR</span>
                    )}
                    {matched && (
                      <>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#16A34A]/10 px-2.5 py-1 text-xs font-semibold text-[#16A34A]"><Sparkles className="h-3.5 w-3.5" /> Recommended for you</span>
                        {(job.match_reasons || []).map((r) => (
                          <span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{r}</span>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-[20px] font-bold text-[#0B2545]">{job.role_title}</h3>
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600"><Building2 className="h-4 w-4 text-slate-400" />{job.organisation_name}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded-full bg-[#1E4FA3]/10 px-3 py-1 text-xs font-semibold text-[#1E4FA3]">{job.qualification}</span>
                      {user && <ShareMenu job={job} />}
                      {isPro && (
                        <button onClick={() => toggleSave(job)} aria-label="Save job" className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white transition-colors hover:border-[#16A34A]/50">
                          <Bookmark className={`h-4.5 w-4.5 ${saved ? "fill-[#16A34A] text-[#16A34A]" : "text-slate-400"}`} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{job.location}</span>
                    <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-slate-400" />{job.employment_type}</span>
                    {job.experience_level && job.experience_level !== "Any" && <span className="flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-slate-400" />{job.experience_level}</span>}
                    {job.salary_range && <span className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-slate-400" />{job.salary_range}</span>}
                  </div>
                  <p className="mt-3 line-clamp-3 text-[15px] leading-relaxed text-slate-600">{job.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-slate-400">Posted {new Date(job.created_at).toLocaleDateString()}</span>
                    <Button onClick={() => apply(job)} className="h-10 rounded-lg bg-[#16A34A] px-5 text-sm font-semibold text-white hover:bg-[#138a3f]">{community ? "I'm Interested" : "Apply Now"}</Button>
                  </div>
                </div>
              );
            })}
            {jobs.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-slate-500">No opportunities match your filters.</div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Opportunities;
